import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription, catchError } from 'rxjs';
import { User } from 'src/app/models/user';
import { LoaderService } from 'src/app/services/loader.service';
import { UserService } from 'src/app/services/user.service';
import { CustomValidators } from 'src/app/shared/validators/custom.validator';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SetLocationModalComponent } from '../set-location-modal/set-location-modal.component';
import { LocationSelection, locationCity, validLocation } from 'src/app/services/location-lookup.service';

@Component({
  selector: 'app-edit-personal-details',
  templateUrl: './edit-personal-details.component.html',
  styleUrls: ['./edit-personal-details.component.scss'],
})
export class EditPersonalDetailsComponent implements OnInit, OnDestroy {
  // #region INPUT, OUTPUT, VIEWCHILD
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('setLocationModal')
  setLocationComponent!: SetLocationModalComponent;
  // #endregion

  user: User = new User();
  formGroup: FormGroup;
  profilePictureFile: File;
  private subscription: Subscription;
  locations: {
    address: string;
    latitude: number;
    longitude: number;
    addressComponent?: google.maps.GeocoderAddressComponent[];
    name?: string;
  }[];
  isDisableSaveBtn = true;
  private modalService = inject(NgbModal);

  constructor(
    private userService: UserService,
    private toastr: ToastrService,
    private loaderService: LoaderService
  ) { }

  openModal(modal: TemplateRef<any>, windowClass: string = '') {
    this.modalService.open(modal, {
      windowClass: `project-modal ${windowClass} set-location-modal-outer`,
      centered: true,
      backdropClass: 'primary-backdrop',
    });
  }

  getPersonalDetails() {
    this.loaderService.setLoading(true);
    this.subscription = this.userService
      .getById(`/api/tenants/myProfile`)
      .subscribe((response: any) => {
        this.user.userId = response.user.id;
        this.user.isMembershipVendor = response.isMembershipVendor;
        this.formGroup.patchValue({
          FirstName: response.user.firstname,
          LastName: response.user.lastname,
          Email: response.user.email,
          Address: response.address1,
          City: response.city,
          Zip: response.zip,
          Phone: response.phoneNumber,
          Location: '',
          Instagram: response.instagramId,
          TikTok: response.tikTokId,
          FaceBook: response.facebookId,
        });
        this.userService.setSearchLocation(
          response.address1,
          response.latitude,
          response.longitude,
          [],
          ""
        );
        if (response.user.photo) {
          this.loaderService.setLoading(true);
          this.userService
            .getUserImage(
              `/api/tenants/me/retriveImage?name=`,
              response.user.photo
            )
            .subscribe((response: any) => {
              this.user.profilePictureUrl = response;
              this.userService.setProfilePictureUrl(response);
              this.loaderService.setLoading(false);
            });
        } else {
          this.loaderService.setLoading(false);
        }
      });
  }

  ngOnInit() {
    this.formGroup = new FormGroup(
      {
        FirstName: new FormControl("", [Validators.required, Validators.maxLength(25)]),
        LastName: new FormControl("", [Validators.required, Validators.maxLength(25)]),
        Email: new FormControl({ value: "", disabled: true }, [Validators.required, Validators.maxLength(50), CustomValidators.emailValidator()]),
        Address: new FormControl("", Validators.maxLength(255)),
        City: new FormControl("", Validators.maxLength(30)),
        Zip: new FormControl("", [Validators.minLength(5), Validators.maxLength(15), CustomValidators.zipCodeValidator()]),
        Phone: new FormControl("", [Validators.minLength(7), Validators.maxLength(15), CustomValidators.PhoneNumberValidator()]),
        Location: new FormControl(""),
        Instagram: new FormControl("", [Validators.maxLength(30)]),
        TikTok: new FormControl("", [CustomValidators.tikTokValidator(), Validators.maxLength(24)]),
        FaceBook: new FormControl("", [CustomValidators.facebookValidator(), Validators.minLength(5), Validators.maxLength(15)]),
      },
      { validators: [CustomValidators.onlyAlphabetsValidator("FirstName"), CustomValidators.onlyAlphabetsValidator("LastName")] }
    );

    this.getPersonalDetails();
  }

  onTextValueChange(field) {
    this.fromGroupTouched();
    this.user[field.text] = field.value;
  }

  fromGroupTouched(isSubmit: boolean = false) {
    if (this.formGroup.touched || isSubmit) {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
      if (this.formGroup.valid) {
        this.isDisableSaveBtn = false;
      }
    }
  }

  openFileInput() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any) {
    this.isDisableSaveBtn = false;
    this.profilePictureFile = event.target.files[0];
    if (!this.profilePictureFile.type.startsWith('image/')) {
      this.toastr.error('Please select image.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.user.profilePictureUrl = reader.result as string;
    };
    reader.readAsDataURL(this.profilePictureFile);
  }

  saveDetails() {
    if (!this.formGroup.touched) {
      this.profileImageSave(true);
      return;
    }

    this.fromGroupTouched(true);
    if (this.formGroup.invalid) {
      return;
    }

    this.user.firstName = this.formGroup.get('FirstName').value;
    this.user.lastName = this.formGroup.get('LastName').value;
    this.user.email = this.formGroup.get('Email').value;
    this.user.address = this.formGroup.get('Address').value;
    this.user.city = this.formGroup.get('City').value;
    this.user.zip = this.formGroup.get('Zip').value;
    this.user.mobileNumber = this.formGroup.get('Phone').value;
    this.user.instagramId = this.formGroup.get('Instagram').value;
    this.user.tikTokId = this.formGroup.get('TikTok').value;
    this.user.faceBookId = this.formGroup.get('FaceBook').value;
    this.user.profilePicture = this.profilePictureFile;
    this.user.Lat = this.locations && this.locations.length > 0 && this.locations[0].latitude !== undefined ? this.locations[0].latitude : 0.00;
    this.user.Lng = this.locations && this.locations.length > 0 && this.locations[0].longitude != undefined ? this.locations[0].longitude : 0.00;

    // Api Implementation is based on Post method using create method
    this.loaderService.setLoading(true);
    this.subscription = this.userService
      .create(`api/tenants/membershipApp/UpdateMembershipUser`, this.user)
      .pipe(
        catchError((error) => {
          if (error.status == 404) {
            throw error;
          }
          else {
            this.loaderService.setLoading(false);
            throw error;
          }
        })
      )
      .subscribe((response) => {
        this.toastr.success('User edited successfully!');
        this.loaderService.setLoading(false);
      });

    if (this.user.profilePicture != null) {
      this.loaderService.setLoading(true);
      const uploadUrl = `api/tenants/membershipApp/userprofile/uploadFile/${this.user.userId}`;
      const formData = new FormData();
      formData.append('Content', this.user.profilePicture);
      this.subscription = this.userService
        .create(uploadUrl, formData)
        .pipe(
          catchError((error) => {
            this.loaderService.setLoading(false);
            throw error;
          })
        )
        .subscribe((response) => {
          this.loaderService.setLoading(false);
        });
    }
    this.profileImageSave();
    this.isDisableSaveBtn = true;
  }

  profileImageSave(isSuccessMsg: boolean = false) {
    if (this.profilePictureFile != null) {
      this.loaderService.setLoading(true);
      const uploadUrl = `api/tenants/membershipApp/userprofile/uploadFile/${this.user.userId}`;
      const formData = new FormData();
      formData.append('Content', this.profilePictureFile);
      this.subscription = this.userService
        .create(uploadUrl, formData)
        .pipe(
          catchError((error) => {
            this.loaderService.setLoading(false);
            throw error;
          })
        )
        .subscribe((response) => {
          if (isSuccessMsg) this.toastr.success('User edited successfully!');
          this.userService.setProfilePictureUrl(this.user.profilePictureUrl);
          this.loaderService.setLoading(false);
        });
    }
  }

  locationEmit(location: LocationSelection) {
    if (!validLocation(location)) return;
    this.locations = [location];
    this.userService.setSearchLocation(location.address, location.latitude, location.longitude, location.addressComponent, location.name);
    this.formGroup.markAsDirty();
    this.formGroup.markAsTouched();
    this.isDisableSaveBtn = false;

    this.formGroup.patchValue({
      Address: location.address,
      City: locationCity(location) || this.formGroup.controls['City'].value || '',
      Zip: location.addressComponent?.find(item => item.types.includes('postal_code'))?.long_name
        || this.formGroup.controls['Zip'].value || '',
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
